import React, { useCallback, useState } from "react";

import { useBookingPermissions } from "../../../../hooks/useBookingPermissions";
import DetachedModal from "../../../Shared/Modals/DetachedModal/DetachedModal";

import {
  ChevronRight,
  Clock,
  FileEdit,
  IdCard,
  Users,
  Utensils
} from "lucide-react";
import { DownloadKitchenSheetContent } from "./DownloadKitchenSheetContent/DownloadKitchenSheetContent";
import { DownloadPlaceCardsContent } from "./DownloadPlaceCardsContent/DownloadPlaceCardsContent";
import { DownloadRunSheetContent } from "./DownloadRunSheetContent/DownloadRunSheetContent";
import { DownloadReservationLabelsContent } from "./DownloadReservationLabelsContent/DownloadReservationLabelsContent";
import { DownloadGuestListContent } from "./DownloadGuestListContent/DownloadGuestListContent";
import { DownloadCustomRunSheetContent } from "./DownloadCustomRunSheetContent/DownloadCustomRunSheetContent";
/**
 * Download/Print Modal Props
 */
type DownloadPrintModalProps = {
  onClose: () => void;
};

export type DownloadType =
  | "GuestList"
  | "KitchenSheet"
  | "PlaceCards"
  | "ReservationLabels"
  | "ShortRunSheet"
  | "CustomRunSheet"
  | "";

/**
 * Download/Print Modal
 *
 * @param param0
 * @returns
 */
export const DownloadPrintModal = ({ onClose }: DownloadPrintModalProps) => {
  const [selectedDownloadType, setSelectedDownloadType] =
    useState<DownloadType>("");
  const { userCanDownloadBookings, userCanDownloadCustomers } =
    useBookingPermissions();

  /**
   * Handle on close
   */
  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleBack = useCallback(() => {
    setSelectedDownloadType("");
  }, [selectedDownloadType]);

  const handleSelectDowhnloadType = useCallback(
    (type: DownloadType) => {
      setSelectedDownloadType(type);
    },
    [selectedDownloadType]
  );

  return (
    <DetachedModal
      handleClose={handleOnClose}
      heading={`Download`}
      width="480px"
    >
      {selectedDownloadType === "" && (
        <>
          <p>Select a category in order to configure the download.</p>

          <div className="SelectList m-2.5">
            {userCanDownloadBookings && userCanDownloadCustomers && (
              <button
                type="button"
                className="SelectListItem my-5 text-white text-[15px] font-normal leading-[19px] border-b border-[#1e2228] pb-5 w-full flex justify-start"
                onClick={() => {
                  setSelectedDownloadType("GuestList");
                }}
              >
                <div className="flex-1 items-center gap-2.5 flex justify-start">
                  <Users className="size-5" />
                  Guest List
                </div>
                <div className="flex-none justify-end flex items-center">
                  {" "}
                  <ChevronRight className="size-5" />
                </div>
              </button>
            )}
            {userCanDownloadBookings && (
              <button
                type="button"
                className="SelectListItem my-5 text-white text-[15px] font-normal leading-[19px] border-b border-[#1e2228] pb-5 w-full flex justify-start"
                onClick={() => {
                  setSelectedDownloadType("KitchenSheet");
                }}
              >
                <div className="flex-1 items-center gap-2.5 flex justify-start">
                  <Utensils className="size-5" />
                  Kitchen Sheet
                </div>
                <div className="flex-none justify-end flex items-center">
                  {" "}
                  <ChevronRight className="size-5" />
                </div>
              </button>
            )}
            <button
              type="button"
              className="SelectListItem my-5 text-white text-[15px] font-normal leading-[19px] border-b border-[#1e2228] pb-5 w-full flex justify-start"
              onClick={() => {
                setSelectedDownloadType("PlaceCards");
              }}
            >
              <div className="flex-1 items-center gap-2.5 flex justify-start">
                <IdCard className="size-5" />
                Place Cards
              </div>
              <div className="flex-none justify-end flex items-center">
                {" "}
                <ChevronRight className="size-5" />
              </div>
            </button>
            <button
              type="button"
              className="SelectListItem my-5 text-white text-[15px] font-normal leading-[19px] border-b border-[#1e2228] pb-5 w-full flex justify-start"
              onClick={() => {
                setSelectedDownloadType("ReservationLabels");
              }}
            >
              <div className="flex-1 items-center gap-2.5 flex justify-start">
                <Clock className="size-5" />
                Reservation Labels
              </div>
              <div className="flex-none justify-end flex items-center">
                {" "}
                <ChevronRight className="size-5" />
              </div>
            </button>
            {userCanDownloadBookings && (
              <button
                type="button"
                className="SelectListItem my-5 text-white text-[15px] font-normal leading-[19px] border-b-0 pb-5 w-full flex justify-start"
                onClick={() => {
                  setSelectedDownloadType("ShortRunSheet");
                }}
              >
                <div className="flex-1 items-center gap-2.5 flex justify-start">
                  <FileEdit className="size-5" />
                  Run sheet
                </div>
                <div className="flex-none justify-end flex items-center">
                  {" "}
                  <ChevronRight className="size-5" />
                </div>
              </button>
            )}
          </div>
        </>
      )}
      {selectedDownloadType === "GuestList" && (
        <DownloadGuestListContent onBack={handleBack} onClose={handleOnClose} />
      )}
      {selectedDownloadType === "PlaceCards" && (
        <DownloadPlaceCardsContent
          onBack={handleBack}
          onClose={handleOnClose}
        />
      )}
      {selectedDownloadType === "KitchenSheet" && (
        <DownloadKitchenSheetContent
          onBack={handleBack}
          onClose={handleOnClose}
        />
      )}
      {selectedDownloadType === "ReservationLabels" && (
        <DownloadReservationLabelsContent
          onBack={handleBack}
          onClose={handleOnClose}
        />
      )}
      {selectedDownloadType === "ShortRunSheet" && (
        <DownloadRunSheetContent
          onBack={handleBack}
          onClose={handleOnClose}
          onSelectDownloadType={handleSelectDowhnloadType}
        />
      )}
      {selectedDownloadType === "CustomRunSheet" && (
        <DownloadCustomRunSheetContent
          onBack={handleBack}
          onClose={handleOnClose}
          onSelectDownloadType={handleSelectDowhnloadType}
        />
      )}
    </DetachedModal>
  );
};
